<template>
  <section class="wrapper-messages-area">
    <b-loading isFullPage :active="isLoading" />
    <div class="columns">
      <h1 class="title column is-size-5 has-text-color-7 mb-0">
        {{ App.MessagesAreaScreen.MESSAGES }}
      </h1>
      <div class="header-buttons column">
        <b-button
          iconLeft="plus"
          :type="isContrast ? 'is-white' : 'is-green-lawn'"
          class="has-text-weight-semibold spacing-py-15 spacing-mr-5"
          tag="router-link"
          :to="newMessage()"
          :expanded="isMobile"
          >{{ App.MessagesAreaScreen.NEW_MESSAGE }}</b-button
        >
      </div>
    </div>

    <div class="is-flex">
      <GenericPanelSearch
        class="flex-1"
        :filterList="filterTags"
        :filterOptions="getFilterOptions"
        :tabPersistName="tabPersistName"
        :placeholderInput="General.TYPE_IT_SEARCH"
        @filters="setFilterList"
        @search-value="setSearchValue"
      />
    </div>

    <div class="level">
      <div class="level-left has-text-left">
        <span class="has-text-color-7 has-text-weight-semibold">
          {{ getPaginationDetails }}
        </span>
      </div>
      <div class="level-right">
        <div v-if="isValidSelection" class="buttons">
          <b-button
            :type="isContrast ? 'is-white-dark' : 'is-green-lawn'"
            role="button"
            @click="openDeleteMessages"
          >
            <span>
              {{ General.DELETE }}
            </span>
          </b-button>
        </div>
      </div>
    </div>

    <section class="spacing-mb-20">
      <b-table
        class="custom-table-administration custom-4-columns-messages-area"
        :data="getListMessageAdm.results"
        :sortIcon="sortIcon"
        :sortIconSize="sortIconSize"
        :checkedRows.sync="selectedMessages"
        :aria-next-label="General.NEXT_PAGE"
        :aria-previous-label="General.PREVIOUS_PAGE"
        :aria-page-label="General.PAGE"
        :aria-current-label="General.CURRENT_PAGE"
        :defaultSortDirection="defaultSortOrder"
        :defaultSort="[sortField, sortOrder]"
        :checkable="!isMobile"
        backendSorting
        type="is-black"
        hoverable
      >
        <template>
          <b-table-column
            v-slot="props"
            :label="App.MessagesAreaScreen.MESSAGE_TITLE"
          >
            <div class="has-text-color-8">
              {{ props.row.title }}
            </div>
          </b-table-column>
          <b-table-column v-slot="props" :label="General.DATE">
            <div class="has-text-color-8">
              {{ new Date(props.row.created_at) | dateFormat("DD/MM/YYYY") }}
            </div>
          </b-table-column>
          <b-table-column
            v-slot="props"
            :label="isMobile ? '' : General.ACTIONS"
          >
            <ActionsGrouper :actions="actionList(props.row)" />
          </b-table-column>
        </template>

        <template #empty>
          <section class="section">
            <NoListMessage v-if="!isLoading" />
            <b-loading v-else :isFullPage="false" :active="isLoading" />
          </section>
        </template>

        <template slot="footer">
          <div
            class="is-flex mb-40"
            :class="{ 'is-flex-direction-column-reverse': isMobile }"
          >
            <b-dropdown
              v-model="perPage"
              aria-role="list"
              position="is-top-right"
              :class="{ 'spacing-mb-40': isMobile }"
              :expanded="isMobile"
              @change="currentPage = 1"
            >
              <template slot="trigger">
                <b-button class="custom-dropdown-button" :expanded="isMobile">
                  {{ perPage }} {{ General.PER_PAGE.toLowerCase() }}

                  <b-icon class="spacing-mx-5" icon="chevron-down" />
                </b-button>
              </template>
              <div class="spacing-p-5 has-text-centered">
                {{ General.RECORDS_FOR_PAGE }}
                <div class="list-page">
                  <b-dropdown-item
                    v-for="option in optionsPagination"
                    :key="option"
                    :value="option"
                  >
                    <div>
                      {{ option }}
                    </div>
                  </b-dropdown-item>
                </div>
              </div>
            </b-dropdown>

            <div
              class="custom-pagination-container"
              :class="{ 'mb-10': isMobile }"
            >
              <b-pagination
                :total="getListMessageAdm.count"
                :perPage="perPage"
                :current.sync="currentPage"
                rangeBefore="1"
                rangeAfter="1"
                order="is-centered"
                class="process-panel-pagination"
                rounded
                :iconPrev="prevIcon"
                :iconNext="nextIcon"
                :aria-next-label="General.NEXT_PAGE"
                :aria-previous-label="General.PREVIOUS_PAGE"
                :aria-page-label="General.PAGE"
                :aria-current-label="General.CURRENT_PAGE"
              />
            </div>
          </div>
        </template>
      </b-table>
    </section>
  </section>
</template>

<script>
import { debounce } from "lodash";
import { mapActions, mapGetters } from "vuex";
import { App, General } from "@/utils/constants";

import GenericPanelSearch from "@/components/GenericPanelSearch";
import NoListMessage from "@/components/NoListMessage";

import ModalViewMessage from "@/views/App/ModalViewMessage.vue";
import ModalWarning from "@/views/App/ModalWarning.vue";

import { dateToISOString } from "@/utils/timeUtility";
import { formatPagination } from "@/utils/transformers";
import { TAB_PERSIST_NAME as PERSIST_NAME } from "@/utils/enumerate";

export default {
  name: "MessagesArea",

  components: {
    GenericPanelSearch,
    NoListMessage
  },

  data() {
    return {
      App,
      General,
      isLoading: false,
      perPage: 10,
      currentPage: 1,
      prevIcon: "chevron-left",
      nextIcon: "chevron-right",
      defaultSortOrder: "desc",
      sortIcon: "menu-up",
      sortIconSize: "is-medium",
      sortField: "name",
      sortOrder: "desc",
      searchValue: "",
      filterTags: {},
      selectedMessages: []
    };
  },

  computed: {
    ...mapGetters({
      user: "getCurrentUser",
      getListMessageAdm: "getListMessageAdm",
      optionsPagination: "getItensPage",
      isContrast: "isContrast",
      isMobile: "isMobile"
    }),

    getPaginationDetails() {
      return formatPagination(
        this.getListMessageAdm,
        this.currentPage,
        this.perPage
      );
    },

    isValidSelection() {
      return this.selectedMessages.length > 1;
    },

    requestBaseData() {
      const date = {};
      if (this.filterTags?.date?.start && this.filterTags?.date?.end) {
        date.start = dateToISOString(this.filterTags?.date.start);
        date.end = dateToISOString(this.filterTags?.date.end);
      }

      return {
        page: this.currentPage,
        size: this.perPage,
        search: this.searchValue,
        start_date: date.start,
        end_date: date.end
      };
    },

    getFilterOptions() {
      return {
        DATE: {
          name: General.PERIOD,
          type: "DATE",
          key: "date",
          unique: true
        }
      };
    },

    tabPersistName() {
      return `${PERSIST_NAME.MESSAGES}_${this.user.id}`;
    }
  },

  watch: {
    currentPage() {
      this.getMessageList();
    },

    perPage() {
      this.getMessageList();
    },

    filterTags: {
      handler() {
        if (this.currentPage === 1) this.getMessageList();
        else this.currentPage = 1;
      },
      deep: true
    },

    searchValue() {
      if (this.currentPage === 1) this.getMessageList();
      else this.currentPage = 1;
    }
  },

  async beforeMount() {
    this.isLoading = true;
    await this.getMessageList();
    this.isLoading = false;
  },

  methods: {
    ...mapActions({
      listMessagesAdm: "listMessagesAdm",
      deleteMessages: "deleteMessages"
    }),

    getMessageList: debounce(async function() {
      this.isLoading = true;
      await this.listMessagesAdm(this.requestBaseData);
      this.isLoading = false;
    }, 1000),

    newMessage() {
      return `enviar-mensagens`;
    },

    setFilterList(values) {
      this.filterTags = values;
      if (this.currentPage != 1) this.currentPage = 1;
    },

    setSearchValue(valueForSearch) {
      this.searchValue = valueForSearch;
    },

    openModalViewMessage(message) {
      this.$buefy.modal.open({
        parent: this,
        component: ModalViewMessage,
        props: {
          messageItem: message
        },
        hasModalCard: true,
        trapFocus: true
      });
    },

    openDeleteMessages(message = null) {
      if (message && message.id) this.selectedMessages = [message];

      const messageIds = this.selectedMessages.map(message => message.id);

      this.$buefy.modal.open({
        parent: this,
        component: ModalWarning,
        props: {
          title: this.General.WARNING,
          message: this.General.TEXTS.WARNING_EXCLUDE_MESSAGE_ADM,
          confirm: async () => {
            await this.deleteMessages(messageIds);
            this.getMessageList(this.requestBaseData);
          },
          isMessageCentered: true
        },
        buttons: [
          { label: General.YES, isConfirm: false, action: true },
          { label: General.NO, isConfirm: true, action: false }
        ],
        hasModalCard: true,
        trapFocus: true
      });
    },

    actionList(item) {
      return [
        {
          label: this.App.MessagesAreaScreen.VIEW_MESSAGE,
          icon: "email-open",
          callback: () => this.openModalViewMessage(item)
        },
        {
          label: this.General.DELETE,
          icon: "delete",
          callback: () => this.openDeleteMessages(item)
        }
      ];
    }
  }
};
</script>

<style scoped lang="scss">
@import "@/assets/scss/variables";

.wrapper-messages-area {
  background-color: $color-1;
  padding: 20px;

  .header-buttons {
    display: flex;
    justify-content: flex-end;
    align-items: center;

    @media screen and (max-width: $tablet) {
      flex-direction: column;
      gap: 5px;
    }
  }
}
</style>
